import React, { useCallback, useState } from 'react';
import { Upload, FileText, TrendingDown, ShieldCheck, Eye, Zap, Lock, X, KeyRound } from 'lucide-react';
import { parseCSV } from '../utils/csvParser';
import { categoriseTransactions } from '../utils/categoriser';
import { Transaction } from '../types';

interface LandingProps {
  onDataReady: (transactions: Transaction[]) => void;
}

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

export function Landing({ onDataReady }: LandingProps) {
  const [dragging, setDragging]               = useState(false);
  const [status, setStatus]                   = useState<'idle' | 'parsing' | 'categorising' | 'done' | 'error'>('idle');
  const [progress, setProgress]               = useState(0);
  const [errorMsg, setErrorMsg]               = useState('');
  const [pendingPdf, setPendingPdf]           = useState<File | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pdfPassword, setPdfPassword]         = useState('');
  const [passwordError, setPasswordError]     = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const processPdf = useCallback(async (file: File, password: string) => {
    setPasswordLoading(true);
    setPasswordError('');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('password', password);
    try {
      const response = await fetch(`${API_BASE}/parse-pdf`, { method: 'POST', body: formData });
      if (response.status === 401) { setPasswordError('Incorrect password. Please try again.'); setPasswordLoading(false); return; }
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err?.detail ?? 'Could not parse PDF.'); }
      const data = await response.json();
      const rawTransactions: Transaction[] = data.transactions.map((t: any, i: number) => ({
        id: `txn-${i}`, date: t.date, description: t.description, amount: t.amount, category: 'Uncategorised', rawRow: {},
      }));
      setShowPasswordModal(false); setPendingPdf(null); setPdfPassword(''); setPasswordLoading(false);
      setStatus('categorising'); setProgress(0);
      const categorised = await categoriseTransactions(rawTransactions, (done, total) => { setProgress(Math.round((done / total) * 100)); });
      setStatus('done'); onDataReady(categorised);
    } catch (err) { setPasswordLoading(false); setPasswordError(err instanceof Error ? err.message : 'Something went wrong.'); }
  }, [onDataReady]);

  const handleFile = useCallback(async (file: File) => {
    setErrorMsg('');
    if (file.name.toLowerCase().endsWith('.pdf')) { setPendingPdf(file); setPdfPassword(''); setPasswordError(''); setShowPasswordModal(true); return; }
    // PDF flow — check if password protected first
    if (file.name.toLowerCase().endsWith('.pdf')) {
      setPendingPdf(file);
      setPdfPassword('');
      setPasswordError('');
      setStatus('parsing');

      try {
        const checkForm = new FormData();
        checkForm.append('file', file);
        const checkRes = await fetch(`${API_BASE}/check-pdf`, { method: 'POST', body: checkForm });
        const checkData = await checkRes.json();

        if (checkData.encrypted) {
          setStatus('idle');
          setShowPasswordModal(true);
        } else {
          // No password needed — parse directly
          setStatus('categorising');
          setProgress(0);
          const formData = new FormData();
          formData.append('file', file);
          formData.append('password', '');
          const response = await fetch(`${API_BASE}/parse-pdf`, { method: 'POST', body: formData });
          if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err?.detail ?? 'Could not parse PDF.'); }
          const data = await response.json();
          const rawTransactions: Transaction[] = data.transactions.map((t: any, i: number) => ({
            id: `txn-${i}`, date: t.date, description: t.description, amount: t.amount, category: 'Uncategorised', rawRow: {},
          }));
          const categorised = await categoriseTransactions(rawTransactions, (done, total) => { setProgress(Math.round((done / total) * 100)); });
          setStatus('done');
          onDataReady(categorised);
        }
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Could not process PDF.');
        setStatus('error');
      }
      return;
    }
    setStatus('parsing');
    try {
      const raw = await parseCSV(file);
      setStatus('categorising'); setProgress(0);
      const categorised = await categoriseTransactions(raw, (done, total) => { setProgress(Math.round((done / total) * 100)); });
      setStatus('done'); onDataReady(categorised);
    } catch (err) { setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.'); setStatus('error'); }
  }, [onDataReady]);

  const onDrop = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragging(false); const file = e.dataTransfer.files[0]; if (file) handleFile(file); }, [handleFile]);
  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if (file) handleFile(file); }, [handleFile]);
  const handlePasswordSubmit = () => { if (pendingPdf) processPdf(pendingPdf, pdfPassword); };
  const handlePasswordCancel = () => { setShowPasswordModal(false); setPendingPdf(null); setPdfPassword(''); setPasswordError(''); };
  const busy = status === 'parsing' || status === 'categorising';

  return (
    <div className="landing">
      <div className="landing-bg-texture" aria-hidden="true" />

      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <button className="modal-close" onClick={handlePasswordCancel}><X size={16} strokeWidth={2} /></button>
            <div className="modal-icon"><KeyRound size={24} strokeWidth={1.5} /></div>
            <h2 className="modal-title">Password protected PDF</h2>
            <p className="modal-sub">Enter your statement password to continue. This is usually your ID number or date of birth.</p>
            <div className="modal-input-wrap">
              <input className="modal-input" type="password" placeholder="Enter PDF password" value={pdfPassword}
                onChange={e => setPdfPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handlePasswordSubmit()} autoFocus />
            </div>
            {passwordError && <p className="modal-error">⚠ {passwordError}</p>}
            <div className="modal-actions">
              <button className="btn-modal-primary" onClick={handlePasswordSubmit} disabled={passwordLoading}>
                {passwordLoading ? 'Processing…' : 'Continue'}
              </button>
              <button className="btn-modal-secondary" onClick={handlePasswordCancel}>Cancel</button>
            </div>
            <p className="modal-privacy"><Lock size={11} strokeWidth={2} />Your password is used once to unlock the PDF and is never stored.</p>
          </div>
        </div>
      )}

      <header className="landing-header">
        <div className="logo"><TrendingDown size={22} strokeWidth={2.5} /><span>imali<span className="logo-accent">yami</span></span></div>
      </header>

      <main className="landing-main">
        <div className="hero">
          <p className="hero-eyebrow">personal finance, honestly</p>
          <h1 className="hero-title">Understand<br />where your money goes.</h1>
          <p className="hero-sub">Upload your bank statement and get an instant breakdown of your spending — categorised automatically, visualised clearly. <br/>No account required.</p>
        </div>

        <div className="features-strip">
          <div className="feature-item">
            <div className="feature-icon"><Zap size={18} strokeWidth={1.8} /></div>
            <div className="feature-text">
              <span className="feature-title">Instant categorisation</span>
              <span className="feature-desc">Every transaction automatically sorted into categories so you can see exactly what you're spending on.</span>
            </div>
          </div>
          <div className="feature-divider" />
          <div className="feature-item">
            <div className="feature-icon"><Eye size={18} strokeWidth={1.8} /></div>
            <div className="feature-text">
              <span className="feature-title">Clear spending dashboard</span>
              <span className="feature-desc">Charts and summaries that actually make sense — not a wall of numbers you have to decode yourself.</span>
            </div>
          </div>
          <div className="feature-divider" />
          <div className="feature-item">
            <div className="feature-icon"><ShieldCheck size={18} strokeWidth={1.8} /></div>
            <div className="feature-text">
              <span className="feature-title">Your data stays yours</span>
              <span className="feature-desc">We never store your bank statement. It's processed and discarded — nothing is saved without your knowledge.</span>
            </div>
          </div>
        </div>

        <div className="upload-section">
          <div className="upload-label-row"><Lock size={13} strokeWidth={2} /><span>Your statement is processed securely and never stored</span></div>
          <div className={`upload-zone ${dragging ? 'dragging' : ''} ${busy ? 'busy' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}>
            {busy ? (
              <div className="upload-status">
                <div className="spinner" />
                <p className="status-label">{status === 'parsing' ? 'Reading your transactions…' : `Categorising… ${progress}%`}</p>
                {status === 'categorising' && <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>}
              </div>
            ) : (
              <>
                <div className="upload-icon-wrap"><Upload size={28} strokeWidth={1.5} /></div>
                <p className="upload-label">Drag your statement here, or{' '}
                  <label className="upload-link">browse<input type="file" accept=".csv,.pdf" onChange={onInputChange} style={{ display: 'none' }} /></label>
                </p>
                <p className="upload-hint">Supports CSV and PDF — FNB, Capitec, TymeBank, Standard Bank, Nedbank, ABSA and more</p>
              </>
            )}
          </div>
          {status === 'error' && <p className="error-msg"><span>⚠</span> {errorMsg}</p>}
        </div>

        <div className="how-it-works">
          <div className="step"><FileText size={18} strokeWidth={1.5} /><span>Export statement from your banking app</span></div>
          <div className="step-divider" />
          <div className="step"><Upload size={18} strokeWidth={1.5} /><span>Drop it here</span></div>
          <div className="step-divider" />
          <div className="step"><TrendingDown size={18} strokeWidth={1.5} /><span>See where your money goes</span></div>
        </div>
      </main>
    </div>
  );
}